import { useMessages } from "@/hooks/api/messages/useMessages";
import { useServices } from "@/hooks/api/services/useServices";
import { MessageRole, MessageType } from "@/models/Message";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  DrawerBody,
  DrawerHeaderTitle,
  InlineDrawer,
  Input,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  BotRegular,
  bundleIcon,
  Dismiss24Regular,
  PersonRegular,
  Search20Filled,
  Search20Regular,
  SearchFilled,
} from "@fluentui/react-icons";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";

const CombinedSearchIcon = bundleIcon(Search20Filled, Search20Regular);

type Props = {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  onResultClick: (messageId: string) => void;
};

type SearchResult = {
  messageId: string;
  role: string;
  name: string;
  date: string;
  content: string;
};

export const SearchDrawer: React.FC<Props> = ({ isDrawerOpen, setIsDrawerOpen, onResultClick }) => {
  // 外部から取得しておく値
  const { threadId } = useParams<{ threadId: string; }>();
  if (!threadId) {
    throw new Error("threadId is required");
  }
  const { data: messages } = useMessages(threadId);
  const { data: services } = useServices();

  // 内部生成する値
  const [searchText, setSearchText] = useState("");
  const [searchIndex, setSearchIndex] = useState<SearchResult[]>([]);
  const [searchResult, setSearchResult] = useState<SearchResult[]>([]);
  const [searchState, setSearchState] = useState("noSearch");

  // 文字列から指定した文字列の前後を抽出する関数
  function extractAroundAll(text: string, searchStr: string, offset = 20) {
    const escapedSearch = searchStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // 前後の文字数が0～10文字になるように、任意の文字（.）を指定
    const regex = new RegExp(`(.{0,${offset}})(${escapedSearch})(.{0,${offset}})`, "g");
    const result = regex.exec(text);
    return result ? "..." + result[1] + "**" + result[2] + "**" + result[3] + "..." : "";
  }

  // SearchIndex更新
  useEffect(() => {
    const index: SearchResult[] = messages!
      .filter((message) => message.type != MessageType.CHOICE)
      .map((message) => {
        // 送信者名組み立て
        let name = services?.getById(message.sender)?.name ?? "";
        if (message.role === MessageRole.user) {
          name = "You";
        }

        // sourceのテキスト情報を結合しておく
        const sourceTitles = message.dataSources?.map((source) => source.section + " " + source.title);
        const content = message.content + "\n" + sourceTitles?.join("\n");

        // 検索用のインデックス
        return {
          messageId: message.id,
          role: message.role,
          name: name,
          date: format(message.createdAt, "MM/dd"),
          content: content,
        };
      });

    // インデックスが変わる(=最後のメッセージIDが異なる)場合のみ、stateを更新する
    if (searchIndex.at(-1)?.messageId !== index.at(-1)?.messageId) {
      setSearchIndex(index);
    }
  }, [messages]);

  // SearchResult更新
  useEffect(() => {
    // 検索条件が空の場合は、検索結果をクリアする
    if (searchText.length === 0) {
      if (searchResult.length > 0) { // state更新→再レンダリングを防ぐために条件をつける
        setSearchResult([]);
      }
      return;
    }

    // 検索結果を取得
    const result: SearchResult[] = searchIndex
      .filter((message) => {
        return message.content.toLowerCase().includes(searchText.toLowerCase());
      })
      .map((message) => {
        // 検索結果の前後を抽出
        const content = extractAroundAll(message.content, searchText);
        return {
          ...message,
          content: content,
        };
      });

    setSearchResult(result);
  }, [searchText, searchIndex]);

  // 表示状態(SearchState)を設定する
  useEffect(() => {
    let newState = "noResult";
    if (searchResult.length > 0) {
      newState = "hasResult";
    } else if (searchText === "") {
      newState = "noSearch";
    }
    if (searchState !== newState) {
      setSearchState(newState);
    }
  }, [searchText, searchResult]);

  const classes = useStyles();
  return (
    <InlineDrawer
      open={isDrawerOpen}
      position="end"
      separator={true}
      size="small"
      className={classes.container}
    >
      <DrawerHeaderTitle
        className={classes.header}
        action={
          <Button
            appearance="subtle"
            aria-label="Close"
            icon={<Dismiss24Regular />}
            onClick={() => setIsDrawerOpen(false)}
          />
        }
      >
        Find in chat
      </DrawerHeaderTitle>
      <DrawerBody className={classes.body}>
        <div className={classes.searchInputArea}>
          <Input
            className={classes.searchInput}
            type="text"
            placeholder="検索文字列を入力"
            contentAfter={<CombinedSearchIcon />}
            appearance="filled-darker"
            onChange={(e) => setSearchText(e.target.value)}
          >
          </Input>
        </div>
        <div className={classes.searchResultArea}>
          {searchState === "noSearch" && (
            <div className={classes.emptySearchContainer}>
              <SearchFilled className={classes.enlargedSearchIcon} />
              <Text>チャット内を検索</Text>
            </div>
          )}
          {searchState === "noResult" && (
            <div className={classes.emptySearchContainer}>
              <Text>検索結果なし</Text>
            </div>
          )}
          {searchState === "hasResult" && (
            <>
              <Text>Result</Text>
              {searchResult.map((result) => {
                return (
                  <Card
                    key={result.messageId}
                    className={classes.searchResultItem}
                    appearance="subtle"
                    onClick={() => onResultClick?.(result.messageId)}
                  >
                    <CardHeader
                      header={
                        <div className={classes.searchResultItemHeader}>
                          <div className={classes.assistantIcon}>
                            {result.role === MessageRole.user
                              ? <Avatar icon={<PersonRegular />} />
                              : <Avatar icon={<BotRegular />} />}
                          </div>
                          <Text className={classes.assistantName}>{result.name}</Text>
                          <Text className={classes.datetime}>{result.date}</Text>
                        </div>
                      }
                    />
                    <div className={classes.searchResultItemContent}>
                      <ReactMarkdown
                        components={{
                          p: (props) => <p className={classes.contentParagraph} {...props} />,
                        }}
                      >
                        {result.content}
                      </ReactMarkdown>
                    </div>
                  </Card>
                );
              })}
            </>
          )}
        </div>
      </DrawerBody>
    </InlineDrawer>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  header: {
    width: "100%",
    boxSizing: "border-box",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    display: "flex",
    justifyContent: "space-between",
  },
  body: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  searchInputArea: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  searchInput: {
    width: "100%",
  },
  searchResultArea: {
    height: "100%",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  emptySearchContainer: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXL,
    gap: tokens.spacingVerticalM,
  },
  enlargedSearchIcon: {
    width: "120px",
    height: "auto",
    color: tokens.colorNeutralForegroundDisabled,
  },
  searchResultHeading: {
    fontSize: tokens.fontSizeBase200,
  },
  searchResultItem: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: 0,
  },
  searchResultItemHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  assistantIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  assistantName: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
  },
  datetime: {
    fontSize: tokens.fontSizeBase200,
  },
  searchResultItemContent: {
    fontSize: tokens.fontSizeBase300,
  },
  contentParagraph: { // ReactMarkdownのデフォルトを上書き
    margin: 0,
  },
});
